import { useTranslations } from 'next-intl';

import type { ConfirmationModalProps } from '@/types/modal';

import { Button } from '../ui/button';
import BaseModal from './BaseModal';

function ConfirmationModal(props: ConfirmationModalProps): React.JSX.Element {
  const translate = useTranslations();
  const {
    title,
    description,
    onOpenChange,
    onConfirm,
    onReject,
    confirmText,
    rejectText,
    isLoadingContent,
    open,
    children,
    className,
    isCritical = false,
    isConfirmDisabled = false,
    isLoadingConfirmation = false,
    confirmIcon,
    footer,
  } = props;

  const finalRejectText = rejectText ?? translate('modal.cancel');

  // For destructive actions in flight, swap label to "deleting…" so the
  // confirm button reads as a status, paired with the Button's spinner.
  const inFlightLabel
    = isCritical === true && isLoadingConfirmation === true
      ? translate('modal.deleting')
      : undefined;
  const finalConfirmText
    = inFlightLabel ?? confirmText ?? translate('modal.confirm');

  const defaultFooter = (
    <>
      <Button
        onClick={(e) => {
          // Stop propagation so row/menu click handlers don't refire.
          e.stopPropagation();
          if (onReject !== undefined) {
            onReject();
          }
          if (onOpenChange !== undefined) {
            onOpenChange(false);
          }
        }}
        variant="text"
        size="default"
        type="button"
      >
        {finalRejectText}
      </Button>
      <Button
        disabled={isConfirmDisabled === true}
        loading={isLoadingConfirmation}
        variant={isCritical === true ? 'error' : 'primary'}
        size="default"
        onClick={(e) => {
          e.stopPropagation();
          if (onConfirm !== undefined) {
            onConfirm();
          }
        }}
        startIcon={confirmIcon}
        type="button"
      >
        {finalConfirmText}
      </Button>
    </>
  );

  return (
    <BaseModal
      onOpenChange={onOpenChange}
      open={open}
      title={title}
      description={description}
      className={className}
      isLoadingContent={isLoadingContent}
      danger={isCritical}
      footer={footer !== undefined && footer !== null ? footer : defaultFooter}
    >
      {children}
    </BaseModal>
  );
}

export default ConfirmationModal;
